import { useCallback } from 'react';
import { Popup } from 'devextreme-react/popup';
import { TextBox } from 'devextreme-react/text-box';
import { SelectBox } from 'devextreme-react/select-box';
import { useDesignerStore } from '../store';
import { flattenTree } from '@calculom/report-designer';
import { ColumnConfig } from './ColumnConfig';
import type { ColumnDefinition, ColumnHeadersRow } from '@calculom/report-designer';
import type { RowNode } from '@calculom/report-designer';

interface ColumnHeaderRowEditorProps {
  node: RowNode & { data: ColumnHeadersRow };
  allColumnHeaderRows: (RowNode & { data: ColumnHeadersRow })[];
}

function ColumnHeaderRowEditor({ node, allColumnHeaderRows }: ColumnHeaderRowEditorProps) {
  const {
    updateNode,
    addColumnToRow,
    updateColumnInRow,
    removeColumnFromRow,
    reorderColumnsInRow,
    setColumnsForRow,
  } = useDesignerStore();

  const data = node.data;

  const handleAddColumn = useCallback(
    (col: ColumnDefinition) => addColumnToRow(node.id, col),
    [node.id, addColumnToRow],
  );

  const handleUpdateColumn = useCallback(
    (id: string, updates: Record<string, unknown>) => updateColumnInRow(node.id, id, updates),
    [node.id, updateColumnInRow],
  );

  const handleRemoveColumn = useCallback(
    (id: string) => removeColumnFromRow(node.id, id),
    [node.id, removeColumnFromRow],
  );

  const handleReorderColumns = useCallback(
    (from: number, to: number) => reorderColumnsInRow(node.id, from, to),
    [node.id, reorderColumnsInRow],
  );

  const handleSetColumns = useCallback(
    (columns: ColumnDefinition[]) => setColumnsForRow(node.id, columns),
    [node.id, setColumnsForRow],
  );

  const otherRows = allColumnHeaderRows.filter((r) => r.id !== node.id);

  const handleCopyFrom = useCallback(
    (sourceNodeId: string) => {
      const source = allColumnHeaderRows.find((r) => r.id === sourceNodeId);
      if (!source) return;
      const copiedColumns = source.data.columns.map((col) => ({
        ...col,
        id: crypto.randomUUID(),
      }));
      setColumnsForRow(node.id, copiedColumns);
    },
    [node.id, allColumnHeaderRows, setColumnsForRow],
  );

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400 font-mono">{node.rowId}</span>
        <TextBox
          value={data.label ?? ''}
          onValueChanged={(e) => updateNode(node.id, { label: e.value || undefined })}
          placeholder="Column Header Label (optional)"
          valueChangeEvent="blur"
          width={240}
        />
        <span className="text-xs text-gray-500">
          {data.columns.length} column{data.columns.length !== 1 ? 's' : ''}
        </span>
        {otherRows.length > 0 && (
          <SelectBox
            items={otherRows.map((r) => ({
              id: r.id,
              label: `Copy from ${r.data.label || r.rowId}`,
            }))}
            displayExpr="label"
            valueExpr="id"
            placeholder="Copy from..."
            width={200}
            onValueChanged={(e) => {
              if (e.value) handleCopyFrom(e.value as string);
            }}
            value={null}
          />
        )}
      </div>
      <ColumnConfig
        columns={data.columns}
        onAddColumn={handleAddColumn}
        onUpdateColumn={handleUpdateColumn}
        onRemoveColumn={handleRemoveColumn}
        onReorderColumns={handleReorderColumns}
        onSetColumns={handleSetColumns}
      />
    </div>
  );
}

function ColumnModalContent() {
  const { nodes } = useDesignerStore();

  const flat = flattenTree(nodes);
  const columnHeaderRows = flat.filter(
    (n): n is RowNode & { data: ColumnHeadersRow } => n.data.type === 'column_headers',
  );

  if (columnHeaderRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-600">No Column Headers on Canvas</p>
        <p className="text-xs text-gray-400 mt-1">
          Drag a "Column Headers" component from the palette onto the canvas first
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2">
      {columnHeaderRows.map((node) => (
        <ColumnHeaderRowEditor key={node.id} node={node} allColumnHeaderRows={columnHeaderRows} />
      ))}
    </div>
  );
}

export function ColumnConfigModal() {
  const { columnModalOpen, setColumnModalOpen } = useDesignerStore();

  const renderContent = useCallback(() => <ColumnModalContent />, []);

  return (
    <Popup
      visible={columnModalOpen}
      onHiding={() => setColumnModalOpen(false)}
      title="Column Configuration"
      width={850}
      height={600}
      showCloseButton
      dragEnabled
      contentRender={renderContent}
    />
  );
}
